import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PageHero, Section, TRADES } from "./_shared";
import { CheckCircle2, Briefcase, Clock, Users, GraduationCap } from "lucide-react";

export default function Trades() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location.hash]);

  return (
    <div data-testid="mitcidco-trades">
      <PageHero
        eyebrow="Courses &amp; Curriculum"
        title="NCVT Trades on offer"
        sub="Two industry-aligned trades — training on modern equipment by experienced instructors, with a curriculum matched to Government ITI norms."
      />

      {/* Summary table */}
      <Section className="!pb-8">
        <div className="overflow-x-auto bg-white border border-amber-100">
          <table className="w-full text-sm" data-testid="mitcidco-trades-summary">
            <thead className="bg-[#7C2D12] text-white text-[10px] tracking-[0.2em] uppercase">
              <tr>
                <th className="text-left px-5 py-3 font-semibold w-12">#</th>
                <th className="text-left px-5 py-3 font-semibold">Trade</th>
                <th className="text-left px-5 py-3 font-semibold">Qualification</th>
                <th className="text-left px-5 py-3 font-semibold">Duration</th>
                <th className="text-center px-5 py-3 font-semibold">Units</th>
                <th className="text-center px-5 py-3 font-semibold">Seats</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-100">
              {TRADES.map((t, i) => (
                <tr key={t.key} className="hover:bg-[#FDF6E3]" data-testid={`mitcidco-trades-row-${t.key}`}>
                  <td className="px-5 py-3 text-slate-400 font-mono text-xs">{String(i + 1).padStart(2, "0")}</td>
                  <td className="px-5 py-3">
                    <a href={`#${t.key}`} className="font-semibold text-[#7C2D12] hover:text-[#B45309]">{t.nameEn}</a>
                    <span className="block text-[11px] text-slate-500">{t.nameMr}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-700">{t.qualification}</td>
                  <td className="px-5 py-3 text-slate-700">{t.duration}</td>
                  <td className="px-5 py-3 text-center font-mono">{t.units}</td>
                  <td className="px-5 py-3 text-center font-mono font-bold text-[#B45309]">{t.intake}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Detail cards */}
      <div className="space-y-14 pb-20">
        {TRADES.map((t) => (<TradeDetail key={t.key} t={t} />))}
      </div>
    </div>
  );
}

function TradeDetail({ t }) {
  return (
    <section id={t.key} className="scroll-mt-20 max-w-6xl mx-auto px-5" data-testid={`mitcidco-trade-detail-${t.key}`}>
      <div className="bg-white border border-amber-100 overflow-hidden">
        <div className="p-6 md:p-8 grid md:grid-cols-[auto_1fr] gap-6 border-b border-amber-100" style={{ borderTopWidth: 4, borderTopColor: t.accent }}>
          <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center text-white font-serif font-black text-3xl" style={{ backgroundColor: t.accent }}>
            {t.nameEn.split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: t.accent }}>NCVT Trade</div>
            <h2 className="font-serif text-2xl md:text-3xl text-[#7C2D12] mt-1 leading-tight">{t.nameEn}</h2>
            <div className="text-sm text-slate-500 italic mt-1">{t.nameMr}</div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Meta icon={<GraduationCap size={13} />} label="Qualification" value={t.qualification} />
              <Meta icon={<Clock size={13} />} label="Duration" value={t.duration} />
              <Meta icon={<Users size={13} />} label="Units" value={String(t.units)} />
              <Meta icon={<Briefcase size={13} />} label="Total Seats" value={String(t.intake)} />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-amber-100">
          <div className="p-6 md:p-8">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#7C2D12]">Curriculum Highlights</div>
            <ul className="mt-4 space-y-2.5">
              {t.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: t.accent }} />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 md:p-8 bg-[#FDF6E3]">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#7C2D12]">Career Prospects · रोजगार / नोकरीच्या संधी</div>
            <ul className="mt-4 space-y-2.5">
              {t.prospects.map((p, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Briefcase size={14} className="mt-0.5 shrink-0" style={{ color: t.accent }} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Meta({ icon, label, value }) {
  return (
    <div className="border border-amber-100 px-3 py-2">
      <div className="text-[9px] tracking-[0.25em] uppercase text-slate-500 flex items-center gap-1">{icon}{label}</div>
      <div className="text-sm font-bold text-[#7C2D12] mt-0.5">{value}</div>
    </div>
  );
}
